import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Zap, AlertTriangle, Clock, FileText, CreditCard } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { openCheckout } from "@/lib/payment";
import DataTable from "@/components/app/DataTable";
import InvoiceBadge, { SubscriptionBadge } from "@/components/app/InvoiceBadge";
import { btnPrimary, cardCls, fmtDate, inr, pageSub, pageTitle } from "@/components/app/ui";

export default function Plan() {
  const [plans, setPlans] = useState([]);
  const [store, setStore] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(null);
  const [payingId, setPayingId] = useState(null);

  const load = useCallback(async () => {
    const [s, b] = await Promise.all([api.get("/vendor/store"), api.get("/vendor/billing")]);
    setStore(s.data); setBilling(b.data);
    window.dispatchEvent(new Event("store:refresh"));
  }, []);
  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
    api.get("/config").then(({ data }) => setCfg(data.payments));
    load();
  }, [load]);

  const unpaid = billing?.outstanding?.count > 0;
  const lapsed = store?.billing?.status === "lapsed";

  const upgrade = async (planId) => {
    if (planId === store.plan) return;
    setBusy(planId);
    try {
      const { data } = await api.post("/vendor/subscription/upgrade", { plan: planId });
      if (data.free) { toast.success(data.message); load(); setBusy(null); return; }
      await openCheckout({
        payment: data.order, name: "Sell24x7", description: `${data.plan.name} plan subscription`,
        onSuccess: async (res) => {
          try {
            await api.post("/vendor/subscription/verify", { ...res, plan: planId });
            toast.success(`You're now on the ${data.plan.name} plan. An invoice for this payment is in your billing history.`);
            load();
          } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
          finally { setBusy(null); }
        },
        onFail: () => { toast.error("Payment failed. You have not been charged."); setBusy(null); },
      });
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setBusy(null); }
  };

  const payInvoice = useCallback(async (inv) => {
    setPayingId(inv.id);
    try {
      const { data } = await api.post(`/vendor/invoices/${inv.id}/pay`);
      await openCheckout({
        payment: data.order, name: "Sell24x7", description: `Invoice ${inv.number} — ${inv.plan_name} plan`,
        onSuccess: async (res) => {
          try {
            const r = await api.post(`/vendor/invoices/${inv.id}/verify`, res);
            toast.success(r.data.message);
            await load();
          } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
          finally { setPayingId(null); }
        },
        onFail: () => { toast.error("Payment failed. You have not been charged."); setPayingId(null); },
      });
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setPayingId(null); }
  }, [load]);

  const columns = useMemo(() => [
    { key: "number", header: "Invoice", cell: (i) => <span className="font-mono font-semibold text-orange-600">{i.number}</span> },
    { key: "plan_name", header: "Plan", hideBelow: "sm" },
    { key: "period_start", header: "Billing period", hideBelow: "md", accessor: (i) => i.period_start, cell: (i) => <span className="text-slate-600 whitespace-nowrap">{fmtDate(i.period_start)} – {fmtDate(i.period_end)}</span> },
    { key: "amount", header: "Amount", align: "right", accessor: (i) => i.amount, cell: (i) => <span className="font-semibold">{inr(i.amount)}</span> },
    { key: "display_status", header: "Status", accessor: (i) => i.display_status, cell: (i) => <InvoiceBadge status={i.display_status} /> },
    { key: "created_at", header: "Created", hideBelow: "lg", accessor: (i) => i.created_at, cell: (i) => <span className="text-slate-600 whitespace-nowrap">{fmtDate(i.created_at)}</span> },
    { key: "due_date", header: "Due", accessor: (i) => i.due_date, cell: (i) => (
      <span className={`whitespace-nowrap ${i.display_status === "overdue" ? "text-red-600 font-semibold" : "text-slate-600"}`}>{fmtDate(i.due_date)}</span>
    ) },
    { key: "paid_at", header: "Paid on", hideBelow: "lg", accessor: (i) => i.paid_at || "", cell: (i) => <span className="text-slate-600 whitespace-nowrap">{fmtDate(i.paid_at)}</span> },
    { key: "actions", header: "", sortable: false, align: "right", cell: (i) => i.status === "pending" ? (
      <button onClick={() => payInvoice(i)} disabled={payingId === i.id} data-testid={`invoice-pay-${i.id}`} className={`${btnPrimary} !h-8 !px-3 !text-[12px]`}>
        <CreditCard size={13} /> {payingId === i.id ? "Processing…" : `Pay ${inr(i.amount)}`}
      </button>
    ) : null },
  ], [payInvoice, payingId]);

  const invoices = billing?.invoices || [];
  const sub = store?.billing;

  return (
    <div className="space-y-4" data-testid="vendor-plan">
      <div>
        <h1 className={pageTitle}>Plan & Billing</h1>
        <p className={pageSub}>
          Current plan: <span className="text-green-600 font-semibold">{store?.plan_info?.name}</span>
          {lapsed && <> · Subscribed to <span className="font-semibold text-neutral-900">{sub?.subscribed_plan_name}</span> <SubscriptionBadge status="lapsed" /></>}
        </p>
        {cfg?.demo_mode && <p className="text-xs text-orange-600 mt-1 font-mono">DEMO PAYMENTS — Razorpay keys not set; payments auto-complete.</p>}
      </div>

      {lapsed && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3 text-[13px] text-red-800" data-testid="plan-lapsed-alert">
          <AlertTriangle size={17} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Your account is on the Free plan because an invoice wasn&apos;t paid within {billing?.grace_days ?? 7} days.</div>
            <div>Pay the overdue invoice below to restore your <b>{sub?.subscribed_plan_name}</b> plan right away.</div>
          </div>
        </div>
      )}
      {!lapsed && unpaid && (
        <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 flex items-start gap-3 text-[13px] text-orange-800" data-testid="plan-unpaid-alert">
          <Clock size={17} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">You have {billing.outstanding.count} unpaid invoice{billing.outstanding.count > 1 ? "s" : ""} ({inr(billing.outstanding.amount)}).</div>
            <div>Pay by <b>{fmtDate(billing.outstanding.next_due)}</b> to keep your {store?.plan_info?.name} plan. After that your account moves to the Free plan until the invoice is paid.</div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {plans.map((p) => {
          const current = store?.plan === p.id;
          const highlight = !!p.popular;
          const subscribed = lapsed && sub?.subscribed_plan === p.id;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`rounded-xl p-5 border shadow-sm transition-all flex flex-col ${current ? "border-green-500 bg-green-50" : subscribed ? "border-red-200 bg-red-50/40" : highlight ? "border-orange-200 bg-white" : "border-slate-200 bg-white"}`}>
              {current && <div className="text-xs font-mono uppercase tracking-widest text-green-600 mb-2 flex items-center gap-1"><Zap size={12} /> Current</div>}
              {subscribed && !current && <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-2">Subscribed · suspended</div>}
              <h3 className="font-display text-[15px] font-bold">{p.name}</h3>
              {p.description && <div className="text-[12px] text-slate-500">{p.description}</div>}
              <div className="mt-2 flex items-end gap-1">
                <span className="text-2xl font-display font-extrabold">₹{p.price.toLocaleString()}</span>
                <span className="text-slate-500 text-[12px] mb-1">/mo</span>
              </div>
              <ul className="mt-4 space-y-1.5 text-[13px] text-slate-600 flex-1">
                {p.features.map((f) => <li key={f} className="flex items-start gap-2"><Check size={15} className="text-green-600 mt-0.5 shrink-0" />{f}</li>)}
              </ul>
              <button onClick={() => upgrade(p.id)} disabled={current || busy === p.id || (unpaid && p.price > 0)} data-testid={`plan-select-${p.id}`}
                title={unpaid && p.price > 0 ? "Pay your outstanding invoice first" : undefined}
                className={`mt-4 w-full h-9 rounded-lg text-[13px] font-semibold transition-colors disabled:opacity-60 ${current ? "bg-slate-100 text-slate-500 cursor-default" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
                {current ? "Current plan" : busy === p.id ? "Processing…" : p.price === 0 ? "Switch to Free" : "Upgrade"}
              </button>
            </div>
          );
        })}
      </div>

      <section className="space-y-2" data-testid="vendor-invoices">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-[14px] font-bold text-neutral-900">Invoices</h2>
            <p className="text-[12px] text-slate-500">Invoices are created every month for paid plans and are due within {billing?.grace_days ?? 7} days. Unpaid invoices move your account to the Free plan until they are paid.</p>
          </div>
        </div>
        <div className={cardCls}>
          <DataTable testId="vendor-invoices-table" exportName="invoices" columns={columns} data={invoices} rowTestId={(i) => `invoice-row-${i.id}`}
            searchKeys={["number", "plan_name"]} searchPlaceholder="Search invoice number or plan…"
            filters={[{ key: "status", label: "All statuses", accessor: (i) => i.display_status, options: [{ value: "paid", label: "Paid" }, { value: "pending", label: "Pending" }, { value: "overdue", label: "Overdue" }, { value: "void", label: "Void" }] }]}
            dateField={{ label: "Created", accessor: (i) => i.created_at }} defaultSort={{ key: "created_at", dir: "desc" }}
            emptyIcon={FileText} emptyTitle="No invoices yet" emptyText="Invoices appear here when you subscribe to a paid plan." />
        </div>
      </section>
    </div>
  );
}
